import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import Theme from '@dagensmat/carrot/Theme';
import { Typography } from '@dagensmat/carrot/Components';
import { Container } from '@dagensmat/carrot/Layout';
import { Trans, useTranslation } from 'react-i18next';
import { getImageUrl } from 'utils/imageUrls';
import { Producer } from 'types/Producer';
import Icon from '_common/components/utils/Icon';
import { useAppSelector } from '../../hooks/reduxHooks';
import { B } from '../../../utils/fonts';

const ClickContainer = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: ${Theme.Spacings.xs}px;
  border-radius: ${Theme.BorderRadius.regular}px;
  padding: ${Theme.Spacings.xs}px 0;
  background-color: ${Theme.Colours.transparent};

  > div {
    text-align: left;
  }

  :focus {
    background-color: ${Theme.Colours.brightPurple};

    > div p,
    > div h3 {
      color: ${Theme.Colours.deepPurple};
    }
  }

  transition: background-color, padding, color, ${Theme.Animations.regular};
  :hover {
    cursor: pointer;
    background-color: ${Theme.Colours.brightPurple};
    padding: ${Theme.Spacings.xs}px;

    > div p,
    > div h3 {
      color: ${Theme.Colours.deepPurple};
    }
  }
`;

const ProducerImage = styled.img`
  width: ${Theme.Spacings.l * 2}px;
  height: ${Theme.Spacings.l * 2}px;
  object-fit: cover;
  border-radius: ${Theme.BorderRadius.regular}px;
  margin-right: ${Theme.Spacings.xs}px;
`;

type ProducerGroupHeaderProps = {
  producer: Pick<
    Producer,
    '_id' | 'image' | 'name' | 'profileArea' | 'handle' | 'minimumOrderAmount'
  >;
};

const ProducerGroupHeader = ({ producer }: ProducerGroupHeaderProps) => {
  const {
    image = {},
    profileArea,
    name,
    handle,
    minimumOrderAmount
  } = producer;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { producerInBasket, isAboveMinimumOrderThreshold } = useAppSelector(
    ({ basket: { producersAboveMinimumOrderAmount }, productsForSale }) => {
      const deliveryDate = productsForSale.selectedDeliveryDate;
      const producerDates = producersAboveMinimumOrderAmount?.[producer._id];
      if (deliveryDate === undefined || producerDates === undefined) {
        return {
          producerInBasket: false
        };
      }

      const isAboveMinimumOrderForProducerAndDate = producerDates[deliveryDate];
      return {
        producerInBasket: true,
        isAboveMinimumOrderThreshold: isAboveMinimumOrderForProducerAndDate
      };
    }
  );

  return (
    <ClickContainer
      onClick={() => {
        return navigate(`/${handle}`, { state: { returnPath: true } });
      }}
    >
      {!isEmpty(image) && (
        <ProducerImage
          alt={producer.name}
          src={
            getImageUrl(image, { width: Theme.Spacings.l * 4, fit: 'max' }) ??
            undefined
          }
        />
      )}
      <div>
        <Typography variant="leadText" as="h3">
          {name}
        </Typography>
        {profileArea && (
          <Typography variant="paragraphSmall" color="secondary">
            {profileArea}
          </Typography>
        )}
        {Boolean(minimumOrderAmount) && (
          <Container flex direction="row" wrap="nowrap" gap="xxs">
            <Typography variant="paragraphSmall">
              <Trans
                t={t}
                i18nKey="MinOrderAmount"
                values={{ minimumOrderAmount }}
                components={{ B: <B /> }}
              />
            </Typography>
            {producerInBasket &&
              (isAboveMinimumOrderThreshold ? (
                <Icon icon="success" fill="success" $size="m" />
              ) : (
                <Icon icon="alert" fill="warning" $size="m" />
              ))}
          </Container>
        )}
      </div>
    </ClickContainer>
  );
};
export default ProducerGroupHeader;
